/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


/**
 * @author Yann Massard (yamass@gmail.com)
 */

import * as $ from "jquery";
import {DEFAULT_TEMPLATES} from "trivial-components";
import {UiItemViewConfig} from "../generated/UiItemViewConfig";
import {UiItemView} from "./UiItemView";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {UiToolbarCommandHandler, UiToolbarConfig, UiToolbarEventSource} from "../generated/UiToolbarConfig";
import {UiToolbarButtonGroupConfig} from "../generated/UiToolbarButtonGroupConfig";
import {UiToolbarButtonConfig} from "../generated/UiToolbarButtonConfig";
import {UiTextCellTemplateConfig} from "../generated/UiTextCellTemplateConfig";
import {UiToolbarDropDownButtonConfig} from "../generated/UiToolbarDropDownButtonConfig";
import {createUiDropDownButtonClickInfoConfig, UiDropDownButtonClickInfoConfig} from "../generated/UiDropDownButtonClickInfoConfig";
import {UiToolAccordion} from "./UiToolAccordion";
import {AbstractUiToolContainer} from "./AbstractUiToolContainer";
import {OrderedDictionary} from "./util/OrderedDictionary";
import {UiDropDown} from "./micro-components/UiDropDown";
import {UiComponent} from "./UiComponent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {doOnceOnClickOutsideElement, enterFullScreen, exitFullScreen, isFullScreen, positionDropDown} from "./Common";
import {AbstractUiToolContainer_ToolbarButtonClickEvent, AbstractUiToolContainer_ToolbarDropDownItemClickEvent} from "../generated/AbstractUiToolContainerConfig";
import {EventFactory} from "../generated/EventFactory";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {Emptyable} from "./util/Emptyable";

enum DropDownInitializationStatus {
	UNINITIALIZED, LOADING, INITIALIZED
}

interface FQButtonId {
	groupId: string,
	buttonId: string
}

interface Button {
	config: UiToolbarButtonConfig;
	visible: boolean;
	shiftedToOverflowDropDown?: boolean;
	$buttonWrapper: JQuery;
	$button: JQuery;
	optimizedWidth?: number;
	$dropDown?: JQuery;
	dropDownItemViewConfig?: UiItemViewConfig;
	dropDownItemView?: UiItemView;
	dropDownInitializationStatus?: DropDownInitializationStatus;
}

type ButtonVisibilities = { fittingButtons: FQButtonId[], nonFittingButtons: FQButtonId[], hiddenButtons: FQButtonId[] };

export class UiToolbar extends UiComponent<UiToolbarConfig> implements Emptyable, UiToolbarCommandHandler, UiToolbarEventSource {

	public readonly onEmptyStateChanged: TeamAppsEvent<boolean> = new TeamAppsEvent<boolean>(this);

	public readonly onToolbarButtonClick: TeamAppsEvent<AbstractUiToolContainer_ToolbarButtonClickEvent> = new TeamAppsEvent<AbstractUiToolContainer_ToolbarButtonClickEvent>(this);
	public readonly onToolbarDropDownItemClick: TeamAppsEvent<AbstractUiToolContainer_ToolbarDropDownItemClickEvent> = new TeamAppsEvent<AbstractUiToolContainer_ToolbarDropDownItemClickEvent>(this);

	public static DEFAULT_TOOLBAR_MAX_HEIGHT = 70;

	private buttonGroupsById: OrderedDictionary<UiButtonGroup> = new OrderedDictionary<UiButtonGroup>();

	private _$toolbar: JQuery;
	private _$backgroundColorDiv: JQuery;
	private _$innerContainer: JQuery;
	private $toolbarFiller: JQuery;
	private _$logo: JQuery;
	private overflowDropDown: UiDropDown;
	private overflowToolAccordion: UiToolAccordion;
	private $overflowDropDownButton: JQuery;

	constructor(config: UiToolbarConfig, context: TeamAppsUiContext) {
		super(config, context);
		this._$toolbar = $(`<div class="UiToolbar teamapps-blurredBackgroundImage" id="${config.id}"></div>`);
		this._$backgroundColorDiv = $('<div class="background-color-div"></div>')
			.css("background-color", config.backgroundColor)
			.appendTo(this._$toolbar);
		this._$innerContainer = $('<div class="inner-container">')
			.appendTo(this._$backgroundColorDiv);

		this.$toolbarFiller = $('<div class="toolbar-filler">').appendTo(this._$innerContainer);

		if (config.logoImage) {
			this.setLogoImage(config.logoImage);
		}

		this.$overflowDropDownButton = $('<div class="overflow-dropdown-button toolbar-button-wrapper"><div class="caret"></div></div>');
		this.$overflowDropDownButton.appendTo(this._$innerContainer);
		this.overflowDropDown = new UiDropDown();
		this.overflowToolAccordion = this.createDropDownAccordion();
		this.overflowDropDown.setContent(this.overflowToolAccordion);
		this.$overflowDropDownButton.mousedown(() => {
			if (!this.overflowDropDown.isOpen) {
				this.overflowDropDown.open(this.$overflowDropDownButton, {
					width: 400
				});
				this.overflowToolAccordion.attachedToDom = true; // obviously...
				this.updateButtonOverflow();
			}
		});

		for (let i = 0; i < config.buttonGroups.length; i++) {
			const buttonGroupConfig = config.buttonGroups[i];
			this.addButtonGroup(buttonGroupConfig);
		}
	}

	private createDropDownAccordion(): UiToolAccordion {
		let accordionConfig = $.extend({}, this._config, {
			buttonGroups: this.buttonGroupsById.values.map(group => $.extend({}, group.getConfig(), {buttons: group.getButtonConfigs()}))
		});
		let toolAccordion = new UiToolAccordion(accordionConfig, this._context);
		toolAccordion.onToolbarButtonClick.addListener(eventObject => {
			if (!eventObject.dropDownClickInfo) { //
				this.overflowDropDown.close();
			}
			this.onToolbarButtonClick.fire(EventFactory.createAbstractUiToolContainer_ToolbarButtonClickEvent(this.getId(), eventObject.groupId, eventObject.buttonId, eventObject.dropDownClickInfo));
		});
		return toolAccordion;
	}


	public getMainDomElement(): JQuery {
		return this._$toolbar;
	}

	public setLogoImage(logoImage: string) {
		if (this._$logo) {
			this._$logo.remove();
			this._$logo = null;
		}
		if (logoImage) {
			this._$logo = $(`<div class="logo" style="background-image: url(${logoImage});">`)
				.appendTo(this._$innerContainer);
		}
	}

	protected onAttachedToDom() {
		this.buttonGroupsById.values.forEach(buttonGroup => buttonGroup.onAttachedToDom());
		// this.overflowToolAccordion.attachedToDom = true; NO!!! this gets deferred till the point where the dropdown is shown!
		this.reLayout();
	}

	public setDropDownData(buttonId: string, itemView: UiItemViewConfig): void {
		this.buttonGroupsById.values.forEach(buttonGroup => buttonGroup.setDropDownData(buttonId, itemView));
		if (this.overflowToolAccordion) {
			this.overflowToolAccordion.setDropDownData(buttonId, itemView);
		}
	}

	public setButtonVisible(buttonId: string, visible: boolean) {
		this.buttonGroupsById.values.forEach(buttonGroup => buttonGroup.setButtonVisible(buttonId, visible));
		if (this.overflowToolAccordion) {
			this.overflowToolAccordion.setButtonVisible(buttonId, visible);
		}
		this.updateButtonOverflow();
	}

	public setButtonGroupVisible(groupId: string, visible: boolean) {
		this.buttonGroupsById.getValue(groupId) && this.buttonGroupsById.getValue(groupId).setVisible(visible);
		if (this.overflowToolAccordion) {
			this.overflowToolAccordion.setButtonGroupVisible(groupId, visible);
		}
		this.updateButtonOverflow();
	}

	public addButtonGroup(buttonGroupConfig: UiToolbarButtonGroupConfig) {
		let changesFromEmptyToNonEmpty = this.empty && buttonGroupConfig != null;
		const existingButtonGroup = this.buttonGroupsById.getValue(buttonGroupConfig.groupId);
		if (existingButtonGroup) {
			this.removeButtonGroup(buttonGroupConfig.groupId);
		}

		const buttonGroup = new UiButtonGroup(buttonGroupConfig, this, this._context);
		this.buttonGroupsById.push(buttonGroupConfig.groupId, buttonGroup);

		let allButtonGroups = this.buttonGroupsById.values.sort((group1, group2) => group1.position - group2.position);
		let otherGroupIndex = 0;
		for (; otherGroupIndex < allButtonGroups.length; otherGroupIndex++) {
			let otherGroup = allButtonGroups[otherGroupIndex];
			if (otherGroup.position > buttonGroupConfig.position) {
				break;
			}
		}

		if (allButtonGroups[otherGroupIndex]) {
			buttonGroup.getMainDomElement().insertBefore(allButtonGroups[otherGroupIndex].getMainDomElement());
		} else {
			buttonGroup.getMainDomElement().insertBefore(this.$toolbarFiller);
		}

		if (this.overflowToolAccordion) {
			this.overflowToolAccordion.addButtonGroup(buttonGroupConfig);
		}
		this.updateButtonOverflow();

		if (this.attachedToDom) {
			buttonGroup.onAttachedToDom();
		}

		// TODO destroy dropdowns

		if (changesFromEmptyToNonEmpty) {
			this.onEmptyStateChanged.fire(false);
		}
	}

	public removeButtonGroup(groupId: string): void {
		let wasAlreadyEmpty = this.empty;

		const buttonGroup = this.buttonGroupsById.getValue(groupId);

		if (buttonGroup) {
			this.buttonGroupsById.remove(groupId);
			buttonGroup.getMainDomElement().remove();
		}

		if (this.overflowToolAccordion) {
			this.overflowToolAccordion.removeButtonGroup(groupId);
		}
		this.updateButtonOverflow();

		if (this.empty && !wasAlreadyEmpty) {
			this.onEmptyStateChanged.fire(true);
		}
	}

	public addButton(groupId: string, buttonConfig: UiToolbarButtonConfig, neighborButtonId: string, beforeNeighbor: boolean) {
		this.buttonGroupsById.getValue(groupId) && this.buttonGroupsById.getValue(groupId).addButton(buttonConfig, neighborButtonId, beforeNeighbor);
		if (this.overflowToolAccordion) {
			this.overflowToolAccordion.addButton(groupId, buttonConfig, neighborButtonId, beforeNeighbor);
		}
		this.updateButtonOverflow();
	}

	public removeButton(buttonId: string): void {
		this.buttonGroupsById.values.forEach(group => group.removeButton(buttonId));
		if (this.overflowToolAccordion) {
			this.overflowToolAccordion.removeButton(buttonId);
		}
		this.updateButtonOverflow();
	}

	updateButtonGroups(buttonGroups: UiToolbarButtonGroupConfig[]): void {
		// TODO implement only if really needed
	}

	public onResize(): void {
		this.updateButtonOverflow();
	}

	private updateButtonOverflow() {
		if (!this.attachedToDom) return;
		let availableWidth = this._$innerContainer[0].offsetWidth;
		this._$innerContainer.addClass("overflow-measurement-mode");
		let logoWidth = 0;
		if (this._$logo) {
			this._$logo.removeClass("hidden");
			logoWidth = this._$logo.outerWidth(true);
		}
		let overflowDropDownButtonWidth = this.$overflowDropDownButton.removeClass("hidden").outerWidth(true);
		let consumedWidth = Math.max(logoWidth, overflowDropDownButtonWidth);
		let hasOverflowingButton = false;
		for (let group of this.buttonGroupsById.values) {
			let buttonVisibilityInfo = group.updateButtonVisibilities(availableWidth - consumedWidth);
			if (this.overflowDropDown.isOpen) {
				buttonVisibilityInfo.fittingButtons.forEach(b => this.overflowToolAccordion.setButtonVisible(b.buttonId, false));
				buttonVisibilityInfo.nonFittingButtons.forEach(b => this.overflowToolAccordion.setButtonVisible(b.buttonId, true));
				buttonVisibilityInfo.hiddenButtons.forEach(b => this.overflowToolAccordion.setButtonVisible(b.buttonId, false));
			}
			consumedWidth += group.isVisible() ? group.getMainDomElement()[0].offsetWidth : 0;
			hasOverflowingButton = hasOverflowingButton || buttonVisibilityInfo.nonFittingButtons.length > 0;
		}
		this.$overflowDropDownButton.toggleClass("hidden", !hasOverflowingButton);
		if (this._$logo) {
			this._$logo.toggleClass("hidden", hasOverflowingButton);
		}
		this._$innerContainer.removeClass("overflow-measurement-mode");
	}

	public get empty() {
		return this.buttonGroupsById.length == 0;
	}

	public destroy(): void {
		// TODO destroy all dropdowns
	}
}

class UiButtonGroup {
	private config: UiToolbarButtonGroupConfig;
	private visible: boolean = true;
	private $buttonGroupWrapper: JQuery;
	private $buttonGroup: JQuery;
	private buttons: OrderedDictionary<Button>;
	private $separator: JQuery;

	constructor(buttonGroupConfig: UiToolbarButtonGroupConfig, private toolbar: UiToolbar, private context: TeamAppsUiContext) {
		const $buttonGroupWrapper = $('<div class="button-group-wrapper"/>');

		const $buttonGroup = $(`<div class="toolbar-button-group" id="${this.toolbarId}_${buttonGroupConfig.groupId}">`)
			.appendTo($buttonGroupWrapper);

		this.config = buttonGroupConfig;
		this.$buttonGroupWrapper = $buttonGroupWrapper;
		this.$buttonGroup = $buttonGroup;
		this.buttons = new OrderedDictionary<Button>();

		for (let j = 0; j < buttonGroupConfig.buttons.length; j++) {
			const buttonConfig: UiToolbarButtonConfig = buttonGroupConfig.buttons[j];
			const button = this.createButton(buttonConfig);
			this.buttons.push(buttonConfig.buttonId, button);
			$buttonGroup.append(button.$buttonWrapper);
		}

		if (buttonGroupConfig.showGroupSeparator) {
			this.$separator = $('<div class="toolbar-group-separator">');
			$buttonGroupWrapper.append(this.$separator);
		}

		this.setVisible(buttonGroupConfig.visible);
	}

	private get toolbarId() {
		return this.toolbar.getId();
	}

	public get position() {
		return this.config.position;
	}

	private createButton(buttonConfig: UiToolbarButtonConfig): Button {
		const $buttonWrapper = $('<div class="toolbar-button-wrapper" data-buttonId="' + buttonConfig.buttonId + '">');
        let renderer = this.context.templateRegistry.createTemplateRenderer(buttonConfig.template);
		const $button = $(renderer.render(buttonConfig.recordData)).appendTo($buttonWrapper);

		if (buttonConfig._type === 'UiToolbarDropDownButton') {
			$buttonWrapper.append(
				'<div class="toolbar-button-caret">' +
				'  <div class="caret"></div>' +
				'</div>'
			);
		}

		const button: Button = {
			config: buttonConfig,
			$buttonWrapper: $buttonWrapper,
			$button: $button,
			visible: buttonConfig.visible
		};

		AbstractUiToolContainer.optimizeButtonWidth(button, (buttonConfig.template as UiTextCellTemplateConfig).maxHeight || UiToolbar.DEFAULT_TOOLBAR_MAX_HEIGHT);

		$buttonWrapper.toggleClass("hidden", !buttonConfig.visible);

		if (buttonConfig._type === 'UiToolbarDropDownButton') {
			this.createDropDown(button);
		}

		return button;
	}

	private createDropDown(button: Button) {
		const dropDownButtonConfig = <UiToolbarDropDownButtonConfig> button.config;
		const $dropDown = $(`<div class="UiToolbar-dropdown teamapps-blurredBackgroundImage pseudo-hidden">`)
			.css({
				"width": dropDownButtonConfig.dropDownPanelWidth + "px"
			})
			.appendTo(document.body);
		button.$dropDown = $dropDown;
		const $backgroundColorDiv = $('<div class="background-color-div">').appendTo($dropDown);

		button.dropDownItemViewConfig = dropDownButtonConfig.staticView;
		if (!dropDownButtonConfig.staticView) {
			$(DEFAULT_TEMPLATES.defaultSpinnerTemplate).appendTo($backgroundColorDiv);
			button.dropDownInitializationStatus = DropDownInitializationStatus.UNINITIALIZED;
		}
	}

	public setDropDownData(buttonId: string, itemViewConfig: UiItemViewConfig) {
		let button = this.buttons.getValue(buttonId);
		button.dropDownItemViewConfig = itemViewConfig;
		this.initDropDownItemView(button);
	}

	private initDropDownItemView(button: Button) {
		if (!button) {
			return;
		}
		const $dropDown = button.$dropDown.find('.background-color-div');
		$dropDown.empty();
		button.dropDownItemView && button.dropDownItemView.destroy();
		button.dropDownItemView = new UiItemView(button.dropDownItemViewConfig, this.context);
		button.dropDownItemView.onItemClicked.addListener(eventObject => {
			this.toolbar.onToolbarDropDownItemClick.fire(EventFactory.createAbstractUiToolContainer_ToolbarDropDownItemClickEvent(this.toolbarId, this.config.groupId, button.config.buttonId, eventObject.groupId, eventObject.itemId));
			button.$dropDown.addClass("pseudo-hidden");
		});
		button.dropDownItemView.getMainDomElement().appendTo($dropDown);
		//button.dropDownItemView.attachedToDom = this.attachedToDom; // TODO
		button.dropDownInitializationStatus = DropDownInitializationStatus.INITIALIZED;
	}

	public setButtonVisible(buttonId: string, visible: boolean) {
		const button = this.buttons.getValue(buttonId);
		if (button) {
			button.$buttonWrapper.toggleClass("hidden", !visible);
			button.visible = visible;
			this.updateVisibility();
		}
	}

	public addButton(buttonConfig: UiToolbarButtonConfig, neighborButtonId: string, beforeNeighbor: boolean) {
		const button = this.createButton(buttonConfig);

		const existingButton = this.buttons.getValue(buttonConfig.buttonId);
		if (existingButton) {
			this.removeButton(buttonConfig.buttonId);
		}

		const neighborButton = this.buttons.getValue(neighborButtonId);
		if (neighborButton) {
			if (beforeNeighbor) {
				button.$buttonWrapper.insertBefore(neighborButton.$buttonWrapper);
				this.buttons.insertBeforeValue(buttonConfig.buttonId, button, neighborButton);
			} else {
				button.$buttonWrapper.insertAfter(neighborButton.$buttonWrapper);
				this.buttons.insertAfterValue(buttonConfig.buttonId, button, neighborButton);
			}
		} else {
			button.$buttonWrapper.appendTo(this.$buttonGroup);
			this.buttons.push(buttonConfig.buttonId, button);
		}

		this.updateVisibility();
	}

	public removeButton(buttonId: string): void {
		const button = this.buttons.getValue(buttonId);

		if (button) {
			this.buttons.remove(buttonId);
			button.$buttonWrapper.remove();
		}

		// TODO destroy dropdown

		this.updateVisibility();
	}

	public setVisible(visible: boolean) {
		this.visible = visible;
		this.updateVisibility();
	}

	public isVisible() {
		return this.visible;
	}

	private updateVisibility() {
		let hasVisibleButton = this.buttons.values.some(button => {
			return button.visible && !button.shiftedToOverflowDropDown;
		});
		this.$buttonGroupWrapper.toggleClass("pseudo-hidden", !(this.visible && hasVisibleButton));
	}

	// TODO remove if not needed!
	public getPreferedWidth() {
		let preferedWidth = this.$separator ? this.$separator.outerWidth(true) : 0;
		return this.buttons.values.reduce((preferedWidth, button) => preferedWidth + (button.visible ? button.optimizedWidth : 0), preferedWidth);
	}

	public updateButtonVisibilities(availableWidth: number): ButtonVisibilities {
		let info: ButtonVisibilities = {
			fittingButtons: [],
			nonFittingButtons: [],
			hiddenButtons: []
		};
		let usedWidth = this.$separator ? this.$separator.outerWidth(true) : 0;
		this.buttons.values.forEach((button) => {
			if (this.visible && button.visible) {
				if (usedWidth + button.optimizedWidth <= availableWidth) {
					button.shiftedToOverflowDropDown = false;
					info.fittingButtons.push({groupId: this.config.groupId, buttonId: button.config.buttonId});
					button.$buttonWrapper.removeClass("hidden");
				} else {
					button.shiftedToOverflowDropDown = true;
					info.nonFittingButtons.push({groupId: this.config.groupId, buttonId: button.config.buttonId});
					button.$buttonWrapper.addClass("hidden");
				}
				usedWidth += button.optimizedWidth;
			} else {
				button.shiftedToOverflowDropDown = false;
				info.hiddenButtons.push({groupId: this.config.groupId, buttonId: button.config.buttonId});
			}
		});
		this.updateVisibility();
		return info;
	}

	public getMainDomElement(): JQuery {
		return this.$buttonGroupWrapper;
	}

	public onAttachedToDom() {
		this.buttons.values.forEach(button => {
			let dropDownItemView = button.dropDownItemView;
			if (dropDownItemView) {
				dropDownItemView.attachedToDom = true;
			}
		});

		let me = this;
		this.getMainDomElement().on("click", ".toolbar-button-wrapper", function (e) {
			let button = me.buttons.getValue((<Element>this).getAttribute("data-buttonId"));

			if (button.config.togglesFullScreenOnComponent) {
				if (isFullScreen()) {
					exitFullScreen();
				} else {
					enterFullScreen(me.context.getComponentById(button.config.togglesFullScreenOnComponent));
				}
			}
			if (button.config.openNewTabWithUrl) {
				window.open(button.config.openNewTabWithUrl, '_blank');
			}

			let dropdownClickInfo: UiDropDownButtonClickInfoConfig = null;
			if (button.$dropDown) {
				let dropdownVisible = !button.$dropDown.is('.pseudo-hidden');
				dropdownClickInfo = createUiDropDownButtonClickInfoConfig(!dropdownVisible, button.dropDownInitializationStatus === DropDownInitializationStatus.INITIALIZED || !!button.dropDownItemViewConfig);
				let dropDownButtonConfig = <UiToolbarDropDownButtonConfig> button.config;
				if (!dropdownVisible) {
					if (button.dropDownItemViewConfig && !button.dropDownItemView) {
						me.initDropDownItemView(button);
					}
					button.$dropDown.css("width", dropDownButtonConfig.dropDownPanelWidth + "px");
					positionDropDown(button.$buttonWrapper, button.$dropDown, {});
					doOnceOnClickOutsideElement(button.$dropDown.add(button.$buttonWrapper), () => button.$dropDown.addClass("pseudo-hidden"));
				} else {
					button.$dropDown.addClass("pseudo-hidden")
				}
				if (button.dropDownInitializationStatus === DropDownInitializationStatus.UNINITIALIZED) {
					button.dropDownInitializationStatus = DropDownInitializationStatus.LOADING;
				}
			}

			me.toolbar.onToolbarButtonClick.fire(EventFactory.createAbstractUiToolContainer_ToolbarButtonClickEvent(me.toolbarId, me.config.groupId, button.config.buttonId, dropdownClickInfo));
		});
	}

	public getButtonConfigs() {
		return this.buttons.values.map(b => b.config);
	}

	public getConfig() {
		return this.config;
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiToolbar", UiToolbar);
